<app-layout>
  <div class="session-manager">
    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="flex justify-content-between align-items-center">
        <div>
          <h1 class="text-2xl font-bold text-900 mb-0">Session Manager</h1>
          <p class="text-600 mt-1">Manage GoVideo sessions, media files, and analytics</p>
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="grid mb-4" *ngIf="stats">
      <div class="col-12 md:col-3">
        <p-card styleClass="h-full">
          <div class="flex justify-content-between align-items-center">
            <div>
              <div class="text-500 font-medium mb-1">Total Sessions</div>
              <div class="text-900 text-2xl font-semibold">{{ stats.totalSessions }}</div>
            </div>
            <div class="bg-blue-100 p-2 border-round">
              <i class="pi pi-video text-blue-500 text-xl"></i>
            </div>
          </div>
        </p-card>
      </div>
      <div class="col-12 md:col-3">
        <p-card styleClass="h-full">
          <div class="flex justify-content-between align-items-center">
            <div>
              <div class="text-500 font-medium mb-1">Active Sessions</div>
              <div class="text-900 text-2xl font-semibold">{{ stats.activeSessions }}</div>
            </div>
            <div class="bg-green-100 p-2 border-round">
              <i class="pi pi-play-circle text-green-500 text-xl"></i>
            </div>
          </div>
        </p-card>
      </div>
      <div class="col-12 md:col-3">
        <p-card styleClass="h-full">
          <div class="flex justify-content-between align-items-center">
            <div>
              <div class="text-500 font-medium mb-1">Media Files</div>
              <div class="text-900 text-2xl font-semibold">{{ stats.totalMediaFiles }}</div>
            </div>
            <div class="bg-purple-100 p-2 border-round">
              <i class="pi pi-images text-purple-500 text-xl"></i>
            </div>
          </div>
        </p-card>
      </div>
      <div class="col-12 md:col-3">
        <p-card styleClass="h-full">
          <div class="flex justify-content-between align-items-center">
            <div>
              <div class="text-500 font-medium mb-1">Storage Used</div>
              <div class="text-900 text-2xl font-semibold">{{ formatFileSize(stats.totalStorageUsed) }}</div>
            </div>
            <div class="bg-orange-100 p-2 border-round">
              <i class="pi pi-database text-orange-500 text-xl"></i>
            </div>
          </div>
        </p-card>
      </div>
    </div>

    <!-- Main Content Card -->
    <p-card>
      <!-- Toolbar -->
      <p-toolbar styleClass="mb-3">
        <div class="p-toolbar-group-start">
          <p-button 
            label="New Session" 
            icon="pi pi-plus" 
            styleClass="p-button-success mr-2"
            (onClick)="createSession()">
          </p-button>
          <p-button 
            label="Actions" 
            icon="pi pi-angle-down"
            iconPos="right"
            [disabled]="selectedSessions.length === 0"
            severity="secondary"
            (onClick)="sessionActionsMenu.toggle($event)">
          </p-button>
          <p-menu #sessionActionsMenu [model]="bulkActionItems" [popup]="true" appendTo="body"></p-menu>
        </div>
        
        <div class="p-toolbar-group-end">
          <span class="p-input-icon-left mr-2">
            <i class="pi pi-search"></i>
            <input 
              pInputText 
              type="text" 
              placeholder="Search sessions..."
              [(ngModel)]="globalFilterValue"
              (input)="applyGlobalFilter($event, dt)"
              class="w-auto">
          </span>
          <p-button 
            icon="pi pi-refresh" 
            styleClass="p-button-outlined"
            (onClick)="loadSessions()"
            [loading]="loading">
          </p-button>
        </div>
      </p-toolbar>

      <!-- Sessions Table -->
      <p-table 
        #dt
        [value]="sessions" 
        [(selection)]="selectedSessions"
        dataKey="sessionId"
        [loading]="loading"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [globalFilterFields]="['sessionId', 'hostUserIdentity', 'guestUserIdentity']"
        styleClass="p-datatable-gridlines"
        responsiveLayout="scroll">
        
        <!-- Table Header -->
        <ng-template pTemplate="header">
          <tr>
            <th style="width: 4rem">
              <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
            </th>
            <th pSortableColumn="sessionId">
              Session ID 
              <p-sortIcon field="sessionId"></p-sortIcon>
            </th>
            <th pSortableColumn="hostUserIdentity">
              Host 
              <p-sortIcon field="hostUserIdentity"></p-sortIcon>
            </th>
            <th pSortableColumn="guestUserIdentity">
              Guest 
              <p-sortIcon field="guestUserIdentity"></p-sortIcon>
            </th>
            <th>Media Files</th>
            <th>Compositions</th>
            <th pSortableColumn="createdAt">
              Created 
              <p-sortIcon field="createdAt"></p-sortIcon>
            </th>
            <th style="width: 12rem">Actions</th>
          </tr>
        </ng-template>

        <!-- Table Body -->
        <ng-template pTemplate="body" let-session>
          <tr>
            <td>
              <p-tableCheckbox [value]="session"></p-tableCheckbox>
            </td>
            <td>
              <span class="font-medium">{{ session.sessionId }}</span>
            </td>
            <td>
              <div class="flex align-items-center gap-2">
                <p-tag 
                  [value]="getRoleLabel(session.hostRole)" 
                  severity="info" 
                  styleClass="text-xs">
                </p-tag>
                <span>{{ session.hostUserIdentity }}</span>
              </div>
            </td>
            <td>
              <div class="flex align-items-center gap-2">
                <p-tag 
                  [value]="getRoleLabel(session.guestRole)" 
                  severity="warn"
                  styleClass="text-xs">
                </p-tag>
                <span>{{ session.guestUserIdentity }}</span>
              </div>
            </td>
            <td>
              <p-badge 
                [value]="session.mediaFiles.length" 
                severity="info">
              </p-badge>
            </td>
            <td>
              <div *ngFor="let comp of session.compositions" class="mb-1">
                <p-tag 
                  [value]="getStatusLabel(comp)" 
                  [severity]="getStatusSeverity(comp.jobStatus)"
                  styleClass="text-xs">
                </p-tag>
              </div>
            </td>
            <td>
              <span class="text-sm">{{ session.createdAt | date:'medium' }}</span>
            </td>
            <td>
              <div class="flex gap-1">
                <p-button 
                  icon="pi pi-play" 
                  styleClass="p-button-rounded p-button-text p-button-sm"
                  pTooltip="Start Session (Host)"
                  [disabled]="!session.hostConnectUrl"
                  (onClick)="startSession(session)">
                </p-button>
                <p-button 
                  icon="pi pi-share-alt" 
                  styleClass="p-button-rounded p-button-text p-button-sm"
                  pTooltip="Share Guest Link"
                  [disabled]="!session.guestConnectUrl"
                  (onClick)="openGuestLinkDialog(session)">
                </p-button>
                <p-button 
                  icon="pi pi-images" 
                  styleClass="p-button-rounded p-button-text p-button-sm"
                  pTooltip="View Media"
                  (onClick)="viewSessionMedia(session)">
                </p-button>
                <p-button 
                  icon="pi pi-ellipsis-v" 
                  styleClass="p-button-rounded p-button-text p-button-sm"
                  (onClick)="showActionMenu($event, session)"
                  [disabled]="false">
                </p-button>
              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Empty State -->
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="8" class="text-center p-4">
              <div class="text-500">
                <i class="pi pi-video text-4xl mb-3 block"></i>
                <p class="text-lg mb-2">No sessions found</p>
                <p class="text-sm">Create your first GoVideo session to get started</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>

    <!-- Action Menu -->
    <p-menu #actionMenu [model]="actionItems" [popup]="true"></p-menu>

    <!-- Media Dialog -->
    <p-dialog 
      header="Session Media" 
      [(visible)]="showMediaDialog" 
      [modal]="true" 
      [style]="{width: '80vw', maxWidth: '1200px'}">
      
      <div *ngIf="currentSession">
        <div class="dialog-header mb-3">
          <h3 class="mb-2">{{ currentSession.sessionId }}</h3>
          
          <!-- Global Controls -->
          <div class="global-controls mb-3">
            <div class="controls-left">
              <div class="grid-size-controls">
                <p-button 
                  icon="pi pi-th-large"
                  [severity]="mediaGridSize === 'small' ? 'primary' : 'secondary'"
                  [outlined]="mediaGridSize !== 'small'"
                  (onClick)="setMediaGridSize('small')"
                  pTooltip="Small thumbnails"
                  tooltipPosition="top"
                  size="small">
                </p-button>
                <p-button 
                  icon="pi pi-stop"
                  [severity]="mediaGridSize === 'medium' ? 'primary' : 'secondary'"
                  [outlined]="mediaGridSize !== 'medium'"
                  (onClick)="setMediaGridSize('medium')"
                  pTooltip="Medium thumbnails"
                  tooltipPosition="top"
                  size="small">
                </p-button>
                <p-button 
                  icon="pi pi-square"
                  [severity]="mediaGridSize === 'large' ? 'primary' : 'secondary'"
                  [outlined]="mediaGridSize !== 'large'"
                  (onClick)="setMediaGridSize('large')"
                  pTooltip="Large thumbnails"
                  tooltipPosition="top"
                  size="small">
                </p-button>
              </div>
            </div>
            
            <div class="controls-right">
              <span class="p-input-icon-left">
                <i class="pi pi-search"></i>
                <input 
                  type="text" 
                  pInputText 
                  placeholder="Search all media..." 
                  [(ngModel)]="globalSearchValue"
                  (input)="filterAllMedia()"
                  class="global-search">
              </span>
            </div>
          </div>
        </div>
        
        <!-- Unified Media Panel Container -->
        <div class="unified-media-container">
          <!-- Media Panel -->
          <div class="media-panel">
            <div class="panel-header">
              <div class="header-left">
                <h4 class="panel-title">
                  <i [class]="selectedMediaType === 'photos' ? 'pi pi-images' : 'pi pi-video'"></i>
                  {{ selectedMediaType === 'photos' ? 'Photos' : 'Videos' }}
                  <p-tag *ngIf="getVisibleMediaItems().length > 0" [value]="getVisibleMediaItems().length + ' items'" [severity]="selectedMediaType === 'photos' ? 'success' : 'info'"></p-tag>
                </h4>
              </div>
              <div class="header-right" *ngIf="getFilteredPhotoItems().length > 0 || getFilteredVideoItems().length > 0">
                <!-- Media Type Selector -->
                <div class="media-type-selector mr-3">
                  <p-selectbutton 
                    [options]="mediaTypeOptions" 
                    [(ngModel)]="selectedMediaType" 
                    (ngModelChange)="onMediaTypeChange()"
                    optionLabel="label"
                    optionValue="value">
                    <ng-template #item let-item>
                      <i [class]="item.icon"></i>
                    </ng-template>
                  </p-selectbutton>
                </div>
                
                <!-- Sort Controls -->
                <div class="sort-controls mr-3">
                  <p-select 
                    [options]="sortFieldOptions" 
                    [(ngModel)]="currentSortBy"
                    (ngModelChange)="sortCurrentMedia()"
                    optionLabel="label" 
                    optionValue="value"
                    placeholder="Sort by..."
                    size="small">
                  </p-select>
                  
                  <p-selectButton 
                    [options]="sortOrderOptions" 
                    [(ngModel)]="currentSortOrder"
                    (ngModelChange)="sortCurrentMedia()"
                    optionLabel="label"
                    optionValue="value"
                    size="small">
                    <ng-template #item let-item>
                      <i [class]="item.icon"></i>
                    </ng-template>
                  </p-selectButton>
                </div>
                
                <!-- Selection Controls -->
                <div class="selection-controls">
                  <div class="flex align-items-center gap-2">
                    <div class="select-all-container">
                      <p-checkbox 
                        [ngModel]="currentSelectAll"
                        (onChange)="toggleCurrentSelectAll()"
                        [binary]="true">
                      </p-checkbox>
                      <label class="cursor-pointer select-all-label" (click)="toggleCurrentSelectAll()">
                        {{ currentSelectAll ? 'Deselect All' : 'Select All' }}
                      </label>
                    </div>
                    <p-button 
                      label="Batch Actions" 
                      icon="pi pi-angle-down"
                      iconPos="right"
                      [disabled]="currentSelectedItems.size === 0"
                      severity="secondary"
                      size="small"
                      (onClick)="currentActionsMenu.toggle($event)">
                    </p-button>
                    <p-menu #currentActionsMenu [model]="currentBatchMenuItems" [popup]="true" appendTo="body"></p-menu>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="panel-content">
              <div *ngIf="mediaLoading" class="text-center p-4">
                <p-progressSpinner></p-progressSpinner>
              </div>
              
              <div *ngIf="!mediaLoading" class="scrollable-content">
                <app-media-gallery
                  [sessionId]="currentSession.sessionId"
                  [media]="getVisibleMediaItems()"
                  [isCoordinator]="false"
                  [showControls]="false"
                  [allowReorder]="false"
                  [allowBatchOperations]="true"
                  [gridSize]="mediaGridSize"
                  [selectedItemIds]="currentSelectedItems"
                  (mediaDeleted)="onMediaDeleted($event)"
                  (mediaUpdated)="onMediaUpdated($event)"
                  (selectionChanged)="onCurrentSelectionChanged($event)">
                </app-media-gallery>
                
                <div *ngIf="getVisibleMediaItems().length === 0" class="text-center p-4 text-500">
                  <i [class]="selectedMediaType === 'photos' ? 'pi pi-images' : 'pi pi-video'" class="text-2xl mb-2 block"></i>
                  No {{ selectedMediaType }} found for this session
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </p-dialog>

    <!-- Guest Link Dialog -->
    <app-guest-link-dialog
      [(visible)]="showGuestLinkDialog"
      [session]="guestLinkSession">
    </app-guest-link-dialog>

    <!-- Session Logs Dialog -->
    <app-session-logs-dialog
      [(visible)]="showLogsDialog"
      [sessionId]="currentSession?.sessionId || null">
    </app-session-logs-dialog>

    <!-- Statistics Dialog -->
    <p-dialog 
      header="Session Statistics" 
      [(visible)]="showStatsDialog" 
      [modal]="true" 
      [style]="{width: '60vw', maxWidth: '800px'}">
      
      <div *ngIf="stats" class="grid">
        <div class="col-12 md:col-6 mb-3">
          <div class="surface-50 p-3 border-round">
            <div class="text-500 font-medium mb-2">Total Sessions</div>
            <div class="text-900 text-xl font-semibold">{{ stats.totalSessions }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <div class="surface-50 p-3 border-round">
            <div class="text-500 font-medium mb-2">Active Sessions</div>
            <div class="text-900 text-xl font-semibold">{{ stats.activeSessions }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <div class="surface-50 p-3 border-round">
            <div class="text-500 font-medium mb-2">Total Media Files</div>
            <div class="text-900 text-xl font-semibold">{{ stats.totalMediaFiles }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <div class="surface-50 p-3 border-round">
            <div class="text-500 font-medium mb-2">Total Compositions</div>
            <div class="text-900 text-xl font-semibold">{{ stats.totalCompositions }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <div class="surface-50 p-3 border-round">
            <div class="text-500 font-medium mb-2">Storage Used</div>
            <div class="text-900 text-xl font-semibold">{{ formatFileSize(stats.totalStorageUsed) }}</div>
          </div>
        </div>
        <div class="col-12 md:col-6 mb-3">
          <div class="surface-50 p-3 border-round">
            <div class="text-500 font-medium mb-2">Avg. Session Duration</div>
            <div class="text-900 text-xl font-semibold">{{ stats.averageSessionDuration }}min</div>
          </div>
        </div>
      </div>
    </p-dialog>
  </div>

  <!-- Session Create/Edit Dialog -->
  <app-session-create-dialog
    [(visible)]="showCreateDialog"
    [session]="currentSession"
    (sessionSaved)="onSessionSaved($event)">
  </app-session-create-dialog>

  <!-- Toast Messages -->
  <p-toast></p-toast>
  
  <!-- Confirmation Dialog -->
  <p-confirmDialog></p-confirmDialog>
</app-layout>
